/**
 * Drawing Editor Functions
 * Handles drawing modal UI and drawing insertion
 */

const drawingInstances = new Map()

/**
 * Show the drawing modal
 * @param {string} modalId - The ID of the modal element
 */
export function showDrawingModal(modalId) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  // Reset canvas
  const canvas = modal.querySelector('canvas')
  if (canvas) {
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Reset drawing state
    const instance = drawingInstances.get(modalId)
    if (instance) {
      instance.resetDrawing()
    }
  }

  // Show the modal
  modal.classList.add('is-open')
}

/**
 * Hide the drawing modal
 * @param {string} modalId - The ID of the modal element
 */
export function hideDrawingModal(modalId) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  modal.classList.remove('is-open')
}

/**
 * Insert drawing from the modal
 * @param {Editor} editor - The TipTap editor instance
 * @param {string} modalId - The ID of the modal element
 */
export function insertDrawingFromModal(editor, modalId) {
  if (!editor) return

  const modal = document.getElementById(modalId)
  if (!modal) return

  const canvas = modal.querySelector('canvas')
  if (!canvas) return

  // Convert canvas to data URL
  const dataUrl = canvas.toDataURL('image/png')

  // Check if Image node type exists in the schema
  const imageNodeType = editor.schema.nodes.image
  if (!imageNodeType) {
    console.error(
      'Image extension is not available in this editor. Please ensure Image extension is included in the editor configuration.'
    )
    hideDrawingModal(modalId)
    return
  }

  // Insert image into editor using insertContent
  editor
    .chain()
    .focus()
    .insertContent({
      type: 'image',
      attrs: { src: dataUrl },
    })
    .run()

  // Hide the modal
  hideDrawingModal(modalId)
}

/**
 * Initialize drawing modal with canvas functionality
 * @param {string} modalId - The ID of the modal element
 * @param {string} canvasId - The ID of the canvas element
 */
export function initDrawingCanvas(modalId, canvasId) {
  // Return existing instance if already initialized
  if (drawingInstances.has(modalId)) {
    return drawingInstances.get(modalId)
  }

  const modal = document.getElementById(modalId)
  const canvas = document.getElementById(canvasId)
  if (!modal || !canvas) return null

  const ctx = canvas.getContext('2d')
  let isDrawing = false
  let currentColor = '#000000'
  let currentStroke = 4

  // Set initial canvas background
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // Drawing functions
  function startDrawing(e) {
    isDrawing = true
    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX || e.touches[0].clientX) - rect.left
    const y = (e.clientY || e.touches[0].clientY) - rect.top
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  function draw(e) {
    if (!isDrawing) return
    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX || e.touches[0].clientX) - rect.left
    const y = (e.clientY || e.touches[0].clientY) - rect.top
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  function stopDrawing() {
    isDrawing = false
  }

  // Canvas event listeners (mouse)
  canvas.addEventListener('mousedown', startDrawing)
  canvas.addEventListener('mousemove', draw)
  canvas.addEventListener('mouseup', stopDrawing)
  canvas.addEventListener('mouseleave', stopDrawing)

  // Canvas event listeners (touch)
  canvas.addEventListener('touchstart', (e) => {
    e.preventDefault()
    startDrawing(e)
  })
  canvas.addEventListener('touchmove', (e) => {
    e.preventDefault()
    draw(e)
  })
  canvas.addEventListener('touchend', (e) => {
    e.preventDefault()
    stopDrawing()
  })

  // Color selector
  modal.querySelectorAll('.drawing-color-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      currentColor = btn.getAttribute('data-color')
      ctx.strokeStyle = currentColor
      modal
        .querySelectorAll('.drawing-color-btn')
        .forEach((b) => b.classList.remove('is-active'))
      btn.classList.add('is-active')
    })
  })

  // Stroke selector
  modal.querySelectorAll('.drawing-stroke-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      currentStroke = parseInt(btn.getAttribute('data-stroke'))
      ctx.lineWidth = currentStroke
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      modal
        .querySelectorAll('.drawing-stroke-btn')
        .forEach((b) => b.classList.remove('is-active'))
      btn.classList.add('is-active')
    })
  })

  // Initialize default color and stroke
  ctx.strokeStyle = currentColor
  ctx.lineWidth = currentStroke
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  const firstColorBtn = modal.querySelector('.drawing-color-btn')
  if (firstColorBtn) {
    firstColorBtn.classList.add('is-active')
  }

  const defaultStrokeBtn = modal.querySelector(
    '.drawing-stroke-btn[data-stroke="4"]'
  )
  if (defaultStrokeBtn) {
    defaultStrokeBtn.classList.add('is-active')
  }

  // Clear function
  function clearCanvas() {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  // Reset function
  function resetDrawing() {
    clearCanvas()
    ctx.strokeStyle = currentColor
    ctx.lineWidth = currentStroke
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }

  const instance = { clearCanvas, resetDrawing }
  drawingInstances.set(modalId, instance)
  return instance
}

/**
 * Handle drawing editor action
 * @param {Editor} editor - The TipTap editor instance
 * @param {string} modalId - The ID of the modal element
 */
export function handleDrawingEditorAction(editor, modalId) {
  showDrawingModal(modalId)
}

/**
 * Set up drawing modal event listeners
 * @param {Editor} editor - The TipTap editor instance
 * @param {string} modalId - The ID of the modal element
 * @param {string} canvasId - The ID of the canvas element
 */
export function setupDrawingListeners(editor, modalId, canvasId) {
  const modal = document.getElementById(modalId)
  if (!modal) return

  // Initialize canvas
  initDrawingCanvas(modalId, canvasId)

  // Handle modal button clicks
  modal.addEventListener('click', (e) => {
    const button = e.target.closest('button')
    if (!button) return

    const action = button.getAttribute('data-action')
    if (!action) return

    e.preventDefault()
    e.stopPropagation()

    if (action === 'closeDrawingModal' || action === 'cancelDrawingModal') {
      hideDrawingModal(modalId)
    } else if (action === 'insertDrawingFromModal') {
      insertDrawingFromModal(editor, modalId)
    } else if (action === 'clearDrawing') {
      const instance = drawingInstances.get(modalId)
      if (instance) {
        instance.clearCanvas()
      }
    }
  })

  // Close modal when clicking overlay
  const overlay = modal.querySelector('.drawing-modal-overlay')
  if (overlay) {
    overlay.addEventListener('click', () => {
      hideDrawingModal(modalId)
    })
  }

  // Close modal on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) {
      hideDrawingModal(modalId)
    }
  })
}
